import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import cx from "classnames";
import remarkGfm from "remark-gfm";
import { useClickOutside } from "src/hooks/useClickOutside";
import { EditIcon } from "@ui-icons";
import type { MessageBubbleProps } from "../../AiToolboxSection.types";
import ChatInput from "../ChatInput/ChatInput";
import type { ChatInputHandle } from "../ChatInput/ChatInput.types";
import MessageBubbleFooter from "./components/MessageBubbleFooter/MessageBubbleFooter";
import { markdownComponents } from "./MessageBubble.markdownComponents";
import "./MessageBubble.css";

const remarkPlugins = [remarkGfm];

const MessageBubble = ({
  content,
  type = "ai",
  isStreaming,
  isLast,
  isLoading,
  onRetry,
  showRetryButton,
  branchInfo,
  onSwitchBranch,
  isEditing,
  onStartEdit,
  onSubmitEdit,
  onCancelEdit,
  "data-test-id": dataTestId,
}: MessageBubbleProps) => {
  const isUser = type === "human";
  const isAi = type === "ai";
  const [editValue, setEditValue] = useState("");
  const chatInputRef = useRef<ChatInputHandle>(null);

  // Memoized to avoid walking the full content array on every parent
  // re-render during streaming.
  const textContent = useMemo(
    () => content.filter((c): c is string => typeof c === "string").join(""),
    [content]
  );

  // Handle click outside to cancel editing
  const handleClickOutsideEdit = useCallback(() => {
    if (isEditing && onCancelEdit) {
      onCancelEdit();
    }
  }, [isEditing, onCancelEdit]);

  const handleOnMouseDownCapture = useClickOutside(handleClickOutsideEdit);

  // Snapshot content and focus once when entering edit mode (not on every content tick).
  useEffect(() => {
    if (isEditing) {
      const id = setTimeout(() => chatInputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [isEditing]);

  const handleCopy = () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard.writeText(textContent).catch((err) => {
      console.warn("Failed to copy AI response", err);
    });
  };

  return (
    <div
      className={cx(
        "chat-message-bubble group flex w-full justify-start flex-col",
        isLast && "min-h-48"
      )}
      data-test-id={dataTestId}
    >
      {!isEditing ? (
        <>
          <div
            className={cx(
              "message-content py-2.5 text-sm text-text-primary whitespace-pre-wrap relative overflow-hidden",
              {
                "bg-info-lightest dark:bg-info-dark px-2 rounded-lg rounded-br-none":
                  isUser,
                "group/edit cursor-pointer hover:bg-info-light/50":
                  isUser && !isLoading,
              }
            )}
            onClick={!isLoading ? onStartEdit : undefined}
          >
            {content.map((chunk, i) =>
              typeof chunk === "string" ? (
                <ReactMarkdown // biome-ignore lint/suspicious/noArrayIndexKey: append-only array, never reordered
                  key={i}
                  remarkPlugins={remarkPlugins}
                  components={markdownComponents}
                >
                  {chunk}
                </ReactMarkdown>
              ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: append-only array, never reordered
                <Fragment key={i}>{chunk}</Fragment>
              )
            )}
            {isStreaming && (
              <span className="inline-block w-1.5 h-4 ml-0.5 bg-neutral animate-pulse rounded-sm" />
            )}
            {isUser && (
              <footer className="mt-2 -mb-1! flex gap-2 justify-end translate-y-6 group-hover/edit:translate-y-0 transition-transform">
                {/* Edit icon on hover */}
                {!isLoading && (
                  <EditIcon
                    fontSize="small"
                    className="-mt-0.5 text-text-secondary"
                  />
                )}
              </footer>
            )}
          </div>

          <MessageBubbleFooter
            isAi={isAi}
            isStreaming={isStreaming}
            showRetryButton={showRetryButton}
            branchInfo={branchInfo}
            onSwitchBranch={onSwitchBranch}
            onRetry={onRetry}
            onCopy={handleCopy}
          />
        </>
      ) : (
        <div
          className="w-full border border-neutral-light dark:border-neutral-dark rounded-lg rounded-br-none px-2 py-0.5"
          onMouseDownCapture={handleOnMouseDownCapture}
        >
          <ChatInput
            ref={chatInputRef}
            initialValue={textContent}
            hasValue={editValue.trim().length > 0}
            onChange={setEditValue}
            onSubmit={() => onSubmitEdit?.(editValue)}
            onCancel={onCancelEdit}
            placeholder="Edit your message..."
          />
          <div className="flex gap-2 mt-1 justify-end">
            <button
              onClick={onCancelEdit}
              className="text-xs text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              Cancel (Esc)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized to prevent past message bubbles from re-rendering on every streaming
// tick. The streaming bubble (passed `streamingContent` and `isStreaming`) still
// re-renders because its `content` reference changes per tick, but completed
// bubbles get stable `content` refs from `useChatStream`'s memoized `uiMessages`.
export default memo(MessageBubble);
